import {
  Container,
  Box,
  makeStyles,
  useTheme,
  useMediaQuery,
  Fade,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LinkIcon from "@material-ui/icons/Link";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import LocationOnOutlinedIcon from "@material-ui/icons/LocationOnOutlined";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import {
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  TwitterShareButton,
  XIcon,
  WhatsappShareButton,
  WhatsappIcon,
  TelegramShareButton,
  TelegramIcon,
  EmailShareButton,
  EmailIcon,
} from "react-share";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Loading } from "../../components/shared/loading";
import parse from "html-react-parser";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const formatLabel = (str) => {
  if (!str) return "";
  return str
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const useStyles = makeStyles((theme) => ({
  // ── HERO ──
  heroSection: {
    backgroundImage:
      'linear-gradient(rgba(76, 0, 76, 0.87), rgba(76, 0, 76, 0.92)), url("/static/our_team.jpg")',
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    minHeight: "32vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      backgroundAttachment: "scroll",
      minHeight: "26vh",
    },
  },
  heroContent: {
    textAlign: "center",
    color: "#ffffff",
    padding: "40px 20px",
    maxWidth: 740,
    [theme.breakpoints.down("xs")]: {
      padding: "28px 16px",
    },
  },
  heroTitle: {
    fontSize: "clamp(1.3rem, 3.5vw, 2rem)",
    fontWeight: 700,
    marginBottom: 12,
    textShadow: "0 2px 15px rgba(0,0,0,0.3)",
    lineHeight: 1.3,
  },
  heroBadges: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 8,
    marginTop: 4,
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: "0.68rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.8)",
    background: "rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: "5px 14px",
    letterSpacing: "0.3px",
    backdropFilter: "blur(4px)",
    "& svg": {
      fontSize: "0.82rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.62rem",
      padding: "4px 10px",
    },
  },
  backBtn: {
    position: "absolute",
    top: 20,
    left: 20,
    color: "rgba(255,255,255,0.6)",
    fontSize: "0.72rem",
    fontWeight: 500,
    textTransform: "none",
    letterSpacing: "0.2px",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#ffffff",
      background: "rgba(255,255,255,0.08)",
    },
    "& svg": {
      fontSize: "1rem",
      marginRight: 4,
    },
    [theme.breakpoints.down("xs")]: {
      top: 12,
      left: 12,
      fontSize: "0.66rem",
    },
  },

  // ── CONTENT ──
  contentSection: {
    padding: "48px 0 0",
    background: "#ffffff",
    [theme.breakpoints.down("xs")]: {
      padding: "28px 0 0",
    },
  },
  contentCard: {
    maxWidth: 800,
    margin: "0 auto",
    padding: "0 24px",
    [theme.breakpoints.down("xs")]: {
      padding: "0 16px",
    },
  },
  jobBody: {
    fontSize: "0.82rem",
    color: "#444",
    lineHeight: 1.85,
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: "#1a1a1a",
      margin: "22px 0 10px",
    },
    "& h1:first-child, & h2:first-child, & h3:first-child": {
      marginTop: 0,
    },
    "& ul, & ol": {
      paddingLeft: 24,
      margin: "10px 0",
    },
    "& li": {
      fontSize: "0.82rem",
      color: "#555",
      marginBottom: 6,
      lineHeight: 1.75,
    },
    "& p": {
      margin: "0 0 12px",
    },
    "& strong, & b": {
      color: "#333",
      fontWeight: 600,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.78rem",
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        fontSize: "0.88rem",
      },
      "& li": {
        fontSize: "0.78rem",
      },
    },
  },

  // ── SHARE BAR ──
  shareBar: {
    maxWidth: 800,
    margin: "0 auto",
    padding: "24px 24px 0",
    display: "flex",
    alignItems: "center",
    gap: 10,
    [theme.breakpoints.down("xs")]: {
      padding: "20px 16px 0",
      gap: 8,
    },
  },
  shareLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "#aaa",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginRight: 2,
  },
  shareBtn: {
    transition: "all 0.2s ease",
    opacity: 0.65,
    "&:hover": {
      opacity: 1,
      transform: "scale(1.12)",
    },
  },

  // ── APPLY FORM ──
  applySection: {
    maxWidth: 800,
    margin: "0 auto",
    padding: "40px 24px 60px",
    [theme.breakpoints.down("xs")]: {
      padding: "28px 16px 48px",
    },
  },
  applyDivider: {
    height: 1,
    background: "linear-gradient(90deg, transparent, rgba(76,0,76,0.12), transparent)",
    marginBottom: 36,
    [theme.breakpoints.down("xs")]: {
      marginBottom: 24,
    },
  },
  applyTitle: {
    fontSize: "1.05rem",
    fontWeight: 600,
    color: "#4c004c",
    marginBottom: 6,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.95rem",
    },
  },
  applySubtitle: {
    fontSize: "0.78rem",
    color: "#888",
    marginBottom: 28,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.72rem",
      marginBottom: 22,
    },
  },
  formRow: {
    display: "flex",
    gap: 16,
    marginBottom: 16,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: 14,
    },
  },
  formField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: "0.82rem",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(76,0,76,0.25)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4c004c",
        borderWidth: 1.5,
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.8rem",
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#4c004c",
    },
  },
  uploadBox: {
    border: "1.5px dashed rgba(76,0,76,0.18)",
    borderRadius: 12,
    padding: "22px 24px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    cursor: "pointer",
    transition: "all 0.3s ease",
    background: "#fafafa",
    "&:hover": {
      borderColor: "#4c004c",
      background: "rgba(76,0,76,0.02)",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "16px 14px",
      gap: 12,
    },
  },
  uploadIcon: {
    color: "#4c004c",
    fontSize: "1.4rem",
    opacity: 0.45,
  },
  uploadText: {
    fontSize: "0.76rem",
    color: "#999",
    "& span": {
      color: "#4c004c",
      fontWeight: 600,
    },
  },
  uploadFileName: {
    fontSize: "0.72rem",
    color: "#4c004c",
    fontWeight: 500,
    marginTop: 3,
  },
  submitBtn: {
    background: "linear-gradient(135deg, #4c004c 0%, #6b1b6b 100%)",
    color: "#ffffff",
    borderRadius: 10,
    padding: "12px 44px",
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.3px",
    boxShadow: "0 4px 18px rgba(76,0,76,0.18)",
    transition: "all 0.35s ease",
    marginTop: 8,
    "&:hover": {
      background: "linear-gradient(135deg, #5a0a5a 0%, #7b2b7b 100%)",
      boxShadow: "0 8px 28px rgba(76,0,76,0.25)",
      transform: "translateY(-2px)",
    },
    "&:disabled": {
      background: "#ccc",
      boxShadow: "none",
      transform: "none",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      padding: "12px 20px",
    },
  },

  // ── NOT FOUND ──
  notFound: {
    textAlign: "center",
    padding: "80px 20px",
  },
  notFoundText: {
    fontSize: "1rem",
    color: "#999",
    fontWeight: 500,
    marginBottom: 16,
  },
  notFoundBtn: {
    color: "#4c004c",
    fontSize: "0.78rem",
    fontWeight: 600,
    textTransform: "none",
    borderColor: "rgba(76,0,76,0.3)",
    borderRadius: 10,
    padding: "8px 24px",
    "&:hover": {
      borderColor: "#4c004c",
      background: "rgba(76,0,76,0.03)",
    },
  },
}));

export const JobDetail = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [isloading, setIsloading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  // Apply form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    linkedin_url: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const [tooltipText, setTooltipText] = useState("Click to copy the URL");

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setIsloading(true);
    setNotFound(false);

    const metaForm = new FormData();
    metaForm.append("page", 9);
    try {
      const metaRes = await Axios.post("/api/getmetadata", metaForm, {
        headers: { headKey: headKey },
      });
      if (metaRes.data.status) {
        setMetaTitle(metaRes.data.data.meta_title);
        setMetaKeywords(metaRes.data.data.meta_keywords);
        setMetaDesc(metaRes.data.data.meta_desc);
      }
    } catch (err) {}

    try {
      const res = await Axios.get("/api/get_jobs", {
        headers: { headKey: headKey },
      });
      if (res.data.status) {
        const found = res.data.data.find(
          (j) => String(j.id) === String(id)
        );
        if (found) {
          setJob(found);
        } else {
          setNotFound(true);
        }
      } else {
        setNotFound(true);
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setIsloading(false);
      handleClick();
    }
  };

  const handleClick = () => {
    const anchor = document.querySelector("#back-to-top-anchor");
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setTooltipText("Link copied to clipboard!");
        setTimeout(() => setTooltipText("Click to copy the URL"), 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      linkedin_url: "",
    });
    setCvFile(null);
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCvChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCvFile(e.target.files[0]);
    }
  };

  const handleApply = async () => {
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.email ||
      !formData.mobile ||
      !cvFile
    ) {
      setSnackMsg("Please fill all required fields and upload your CV.");
      setSnackSeverity("error");
      setSnackOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      fd.append("first_name", formData.first_name);
      fd.append("last_name", formData.last_name);
      fd.append("email", formData.email);
      fd.append("mobile", formData.mobile);
      fd.append("cv", cvFile);
      if (formData.linkedin_url) {
        fd.append("linkedin_url", formData.linkedin_url);
      }

      const res = await Axios.post("/api/apply_job", fd, {
        headers: { headKey: headKey, "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        setSnackMsg(res.data.msg || "Applied successfully!");
        setSnackSeverity("success");
        setSnackOpen(true);
        resetForm();
      } else {
        const msg =
          res.data.msg || "Something went wrong. Please try again.";
        const isDuplicate = msg.toLowerCase().includes("already applied");
        setSnackMsg(msg);
        setSnackSeverity(isDuplicate ? "warning" : "error");
        setSnackOpen(true);
      }
    } catch (err) {
      setSnackMsg("Something went wrong. Please try again.");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (isloading) {
    return (
      <div>
        <Helmet>
          <title>{metaTitle || "Careers"}</title>
        </Helmet>
        <Loading />
      </div>
    );
  }

  if (notFound || !job) {
    return (
      <div>
        <Helmet>
          <title>Job Not Found</title>
        </Helmet>
        <Box className={classes.heroSection}>
          <Button className={classes.backBtn} onClick={() => navigate("/careers")}>
            <ArrowBackIcon /> Back to Careers
          </Button>
          <Fade in={true} timeout={1000}>
            <Box className={classes.heroContent}>
              <Typography className={classes.heroTitle}>Careers</Typography>
            </Box>
          </Fade>
        </Box>
        <Box className={classes.notFound}>
          <Typography className={classes.notFoundText}>
            This job posting is no longer available or doesn't exist.
          </Typography>
          <Button
            variant="outlined"
            className={classes.notFoundBtn}
            onClick={() => navigate("/careers")}
          >
            Browse Open Positions
          </Button>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>
          {job.title}
          {metaTitle ? ` - ${metaTitle}` : ""}
        </title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords} />
      </Helmet>

      {/* Hero */}
      <Box className={classes.heroSection}>
        <Button className={classes.backBtn} onClick={() => navigate("/careers")}>
          <ArrowBackIcon /> Back to Careers
        </Button>
        <Fade in={true} timeout={1000}>
          <Box className={classes.heroContent}>
            <Typography className={classes.heroTitle}>{job.title}</Typography>
            <Box className={classes.heroBadges}>
              {job.job_type && (
                <Box className={classes.heroBadge}>
                  <BusinessCenterIcon />
                  {formatLabel(job.job_type)}
                </Box>
              )}
              {job.work_type && (
                <Box className={classes.heroBadge}>
                  <WorkOutlineIcon />
                  {formatLabel(job.work_type)}
                </Box>
              )}
              {job.location && (
                <Box className={classes.heroBadge}>
                  <LocationOnOutlinedIcon />
                  {job.location}
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Job Description */}
      <Box className={classes.contentSection}>
        <Fade in={true} timeout={800}>
          <Box className={classes.contentCard}>
            <Box className={classes.jobBody}>
              {job.description ? parse(job.description) : ""}
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Share Bar */}
      <Box className={classes.shareBar}>
        <Typography className={classes.shareLabel}>Share</Typography>
        <FacebookShareButton url={window.location.href} className={classes.shareBtn}>
          <FacebookIcon size={26} round />
        </FacebookShareButton>
        <LinkedinShareButton url={window.location.href} className={classes.shareBtn}>
          <LinkedinIcon size={26} round />
        </LinkedinShareButton>
        <TwitterShareButton url={window.location.href} className={classes.shareBtn}>
          <XIcon size={26} round />
        </TwitterShareButton>
        <WhatsappShareButton url={window.location.href} className={classes.shareBtn}>
          <WhatsappIcon size={26} round />
        </WhatsappShareButton>
        <TelegramShareButton url={window.location.href} className={classes.shareBtn}>
          <TelegramIcon size={26} round />
        </TelegramShareButton>
        <EmailShareButton url={window.location.href} className={classes.shareBtn}>
          <EmailIcon size={26} round />
        </EmailShareButton>
        <Tooltip title={tooltipText} arrow>
          <IconButton onClick={handleCopy} size="small" style={{ opacity: 0.6 }}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Apply Form */}
      <Box className={classes.applySection}>
        <Box className={classes.applyDivider} />
        <Fade in={true} timeout={1000}>
          <Box>
            <Typography className={classes.applyTitle}>
              Apply for this Position
            </Typography>
            <Typography className={classes.applySubtitle}>
              Fill in your details below and we'll get back to you shortly.
            </Typography>

            <Box className={classes.formRow}>
              <TextField
                label="First Name *"
                name="first_name"
                variant="outlined"
                size="small"
                value={formData.first_name}
                onChange={handleFormChange}
                className={classes.formField}
              />
              <TextField
                label="Last Name *"
                name="last_name"
                variant="outlined"
                size="small"
                value={formData.last_name}
                onChange={handleFormChange}
                className={classes.formField}
              />
            </Box>
            <Box className={classes.formRow}>
              <TextField
                label="Email *"
                name="email"
                type="email"
                variant="outlined"
                size="small"
                value={formData.email}
                onChange={handleFormChange}
                className={classes.formField}
              />
              <TextField
                label="Mobile *"
                name="mobile"
                variant="outlined"
                size="small"
                value={formData.mobile}
                onChange={handleFormChange}
                className={classes.formField}
              />
            </Box>
            <Box className={classes.formRow}>
              <TextField
                label="LinkedIn URL (Optional)"
                name="linkedin_url"
                variant="outlined"
                size="small"
                value={formData.linkedin_url}
                onChange={handleFormChange}
                className={classes.formField}
              />
            </Box>

            {/* CV Upload */}
            <input
              type="file"
              id="cv-upload"
              accept=".pdf,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleCvChange}
            />
            <label htmlFor="cv-upload" style={{ display: "block", marginBottom: 24 }}>
              <Box className={classes.uploadBox}>
                <CloudUploadIcon className={classes.uploadIcon} />
                <Box>
                  <Typography className={classes.uploadText}>
                    <span>Upload your CV</span> — PDF or DOC (Required)
                  </Typography>
                  {cvFile && (
                    <Typography className={classes.uploadFileName}>
                      {cvFile.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            </label>

            <Button
              className={classes.submitBtn}
              onClick={handleApply}
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={20} style={{ color: "#fff" }} />
              ) : (
                "Submit Application"
              )}
            </Button>
          </Box>
        </Fade>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          variant="filled"
          style={{
            borderRadius: 10,
            fontSize: "0.8rem",
            fontWeight: 500,
          }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};
